/**
 * The 15×15 board: role="grid" with roving tabindex, the draft overlay,
 * last-move glow, hints and the keyboard cursor. Pointer input is delegated
 * at the grid level so the 225 memoised cells receive primitives only.
 */
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import { BOARD_SIZE, CENTER, PREMIUM_LAYOUT, hintCells } from '@wordloom/engine';
import type { GameView, Seat } from '@wordloom/engine';
import { cellKey } from '../game/draft';
import { dragController } from '../game/drag';
import { handleBoardKey, tapCell } from '../game/interactions';
import type { PreviewModel } from '../game/preview';
import { DEFAULT_CURSOR, useDraftStore } from '../state/draftStore';
import { Cell, type CellProps } from './Cell';
import './Board.css';

interface BoardProps {
  game: GameView;
  you: Seat;
  names: [string, string];
  preview: PreviewModel;
  /** Double-tap on an empty square (mobile zoom toggle). */
  onDoubleTap?: (row: number, col: number) => void;
}

/** Column letters A–O, matching `cellName` (e.g. "H8"). */
const COLUMN_LABELS = Array.from({ length: BOARD_SIZE }, (_, i) => String.fromCharCode(65 + i));

function parseCell(el: HTMLElement): { row: number; col: number } | null {
  const [r, c] = (el.dataset.cell ?? '').split(',').map(Number);
  if (r === undefined || c === undefined || Number.isNaN(r) || Number.isNaN(c)) return null;
  return { row: r, col: c };
}

/** Committed tile ids on the board. */
function tileIds(game: GameView): Set<string> {
  const ids = new Set<string>();
  for (const row of game.board) for (const t of row) if (t) ids.add(t.id);
  return ids;
}

export function Board({ game, you, names, preview, onDoubleTap }: BoardProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const lastTap = useRef<{ t: number; key: string } | null>(null);

  const placements = useDraftStore((s) => s.placements);
  const cursor = useDraftStore((s) => s.cursor);
  const direction = useDraftStore((s) => s.direction);
  const selectedId = useDraftStore((s) => s.selectedId);
  const draggingId = useDraftStore((s) => s.draggingId);
  const droppedId = useDraftStore((s) => s.droppedId);
  const invalidCells = useDraftStore((s) => s.invalidCells);
  const shakeNonce = useDraftStore((s) => s.shakeNonce);
  const pending = useDraftStore((s) => s.pending);

  useEffect(() => {
    dragController.registerGrid(gridRef.current);
    return () => dragController.registerGrid(null);
  }, []);

  // Tiles that appeared since the previous board: the opponent's drop in
  // (staggered), mine "settle". Nothing animates on the first render.
  const prevIds = useRef<Set<string> | null>(null);
  const arrivals = useMemo(() => {
    const map = new Map<string, { mine: boolean; index: number }>();
    const prev = prevIds.current;
    if (!prev) return map;
    let index = 0;
    for (const row of game.board) {
      for (const t of row) {
        if (t && !prev.has(t.id)) map.set(t.id, { mine: t.playedBy === you, index: index++ });
      }
    }
    return map;
  }, [game.board, you]);
  useEffect(() => {
    prevIds.current = tileIds(game);
  }, [game]);

  const lastMove = useMemo(() => {
    const set = new Set<string>();
    const last = game.history[game.history.length - 1];
    if (last?.type === 'play') for (const t of last.tiles) set.add(cellKey(t.row, t.col));
    return set;
  }, [game.history]);

  const hints = useMemo(() => {
    const set = new Set<string>();
    if (!(selectedId || draggingId) || game.status !== 'playing') return set;
    for (const c of hintCells(game.board, placements)) set.add(cellKey(c.row, c.col));
    return set;
  }, [selectedId, draggingId, game.board, game.status, placements]);

  const bad = useMemo(() => {
    const set = new Set<string>();
    for (const c of invalidCells) set.add(cellKey(c.row, c.col));
    // Live preview (UX-SPEC §5): whatever makes the move invalid is ringed —
    // unknown words' tiles, and for geometry errors the squares the engine
    // cites: a gap, the ★ on a first move, tiles out of line or unconnected.
    if (preview.state === 'invalid') {
      for (const c of preview.badCells) set.add(cellKey(c.row, c.col));
    }
    return set;
  }, [invalidCells, preview]);

  const rackById = useMemo(() => new Map(game.rack.map((t) => [t.id, t])), [game.rack]);
  const draftByCell = useMemo(() => new Map(placements.map((p) => [cellKey(p.row, p.col), p])), [placements]);
  const focusCell = cursor ?? DEFAULT_CURSOR;
  // Once the game is over the cursor square stays the roving focus stop (its
  // ring is the keyboard focus indicator), but there is no typing direction.
  const typing = game.status === 'playing';
  const boardEmpty = useMemo(() => game.board.every((row) => row.every((t) => t === null)), [game.board]);
  const centerCovered = draftByCell.has(cellKey(CENTER, CENTER));

  const cells: CellProps[] = [];
  /** Flagged squares with no tile on them (a gap, the ★). */
  const badEmpty: string[] = [];
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      const key = cellKey(row, col);
      const committed = game.board[row]?.[col] ?? null;
      const draft = committed ? undefined : draftByCell.get(key);
      const draftTile = draft ? rackById.get(draft.tileId) : undefined;
      const arrival = committed ? arrivals.get(committed.id) : undefined;
      const isBad = bad.has(key);
      if (isBad && !committed && !draft) badEmpty.push(key);
      cells.push({
        row,
        col,
        premium: PREMIUM_LAYOUT[row]?.[col] ?? null,
        center: row === CENTER && col === CENTER,
        content: committed ? 'committed' : draft ? 'draft' : 'empty',
        tileId: committed?.id ?? draft?.tileId ?? null,
        letter: committed?.letter ?? (draftTile ? (draftTile.blank ? (draft?.letter ?? '') : draftTile.letter) : ''),
        value: committed?.value ?? draftTile?.value ?? 0,
        blank: committed?.blank ?? draftTile?.blank ?? false,
        playedBy: committed ? (names[committed.playedBy] ?? null) : null,
        tabbable: focusCell.row === row && focusCell.col === col,
        cursor: cursor !== null && cursor.row === row && cursor.col === col,
        // Only the cursor square shows the typing direction; a constant
        // elsewhere keeps Space (toggle) from re-rendering all 225 squares.
        direction: cursor !== null && cursor.row === row && cursor.col === col ? (typing ? direction : null) : 'H',
        hint: hints.has(key),
        bad: isBad,
        last: lastMove.has(key),
        dropIndex: arrival && !arrival.mine ? arrival.index : -1,
        settle: arrival?.mine === true,
        dragging: draft !== undefined && draft.tileId === draggingId,
        shared: draft !== undefined && draft.tileId !== droppedId,
        pending: draft !== undefined && pending,
        shakeNonce: draft && isBad ? shakeNonce : 0,
        starPulse: row === CENTER && col === CENTER && boardEmpty && !centerCovered && game.status === 'playing',
      });
    }
  }

  // An empty flagged square has no tile to ring and Cell draws nothing for
  // it, so the square itself is marked: `data-bad`, set straight on the DOM
  // like the drag controller's `data-drop` (Cell.css draws the ring).
  const badEmptyKey = badEmpty.join(' ');
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const keys = new Set(badEmptyKey ? badEmptyKey.split(' ') : []);
    for (const el of grid.querySelectorAll<HTMLElement>('[data-cell]')) {
      if (keys.has(el.dataset.cell ?? '')) el.dataset.bad = '';
      else delete el.dataset.bad;
    }
  }, [badEmptyKey]);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    const tileEl = (e.target as HTMLElement).closest<HTMLElement>('[data-draft-tile]');
    const cellEl = tileEl?.closest<HTMLElement>('[data-cell]');
    const pos = cellEl ? parseCell(cellEl) : null;
    const tileId = tileEl?.dataset.tileId;
    if (!tileEl || !pos || !tileId) return;
    dragController.begin({ nativeEvent: e.nativeEvent, currentTarget: tileEl }, { kind: 'board', tileId, ...pos });
  };

  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.detail === 0) return; // keyboard activation is handled by onKeyDown
    const target = e.target as HTMLElement;
    if (target.closest('[data-draft-tile]')) return; // taps on draft tiles go through the drag controller
    const cellEl = target.closest<HTMLElement>('[data-cell]');
    const pos = cellEl ? parseCell(cellEl) : null;
    if (!cellEl || !pos) return;
    const draft = useDraftStore.getState();
    const now = performance.now();
    const key = cellEl.dataset.cell ?? '';
    if (onDoubleTap && !draft.selectedId && lastTap.current && lastTap.current.key === key && now - lastTap.current.t < 320) {
      lastTap.current = null;
      onDoubleTap(pos.row, pos.col);
      return;
    }
    lastTap.current = { t: now, key };
    if (game.board[pos.row]?.[pos.col]) {
      draft.setCursor(pos);
      return;
    }
    tapCell(pos.row, pos.col);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (handleBoardKey(e)) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onFocus = (e: FocusEvent<HTMLDivElement>) => {
    const cellEl = (e.target as HTMLElement).closest<HTMLElement>('[data-cell]');
    const pos = cellEl ? parseCell(cellEl) : null;
    const draft = useDraftStore.getState();
    if (pos && (draft.cursor?.row !== pos.row || draft.cursor?.col !== pos.col)) draft.setCursor(pos);
  };

  const rows = [];
  for (let row = 0; row < BOARD_SIZE; row++) {
    rows.push(
      <div role="row" className="board__row" key={row} aria-rowindex={row + 1}>
        {cells.slice(row * BOARD_SIZE, (row + 1) * BOARD_SIZE).map((c) => (
          <Cell key={c.col} {...c} />
        ))}
      </div>,
    );
  }

  return (
    <div className="board-frame">
      <div className="board-frame__cols" aria-hidden="true">
        {COLUMN_LABELS.map((l) => (
          <span key={l}>{l}</span>
        ))}
      </div>
      <div className="board-frame__rows" aria-hidden="true">
        {COLUMN_LABELS.map((_, i) => (
          <span key={i}>{i + 1}</span>
        ))}
      </div>
      <div
        ref={gridRef}
        className="board"
        role="grid"
        aria-label="Board"
        aria-rowcount={BOARD_SIZE}
        aria-colcount={BOARD_SIZE}
        aria-describedby="board-help"
        onPointerDown={onPointerDown}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        data-hinting={hints.size > 0 || undefined}
      >
        {rows}
      </div>
      <p id="board-help" className="sr-only">
        Arrow keys move the cursor. Type a letter to place a tile, Space switches between across and down, Backspace takes
        back the last tile, Enter plays your word.
      </p>
    </div>
  );
}
